import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchOrder } from "../api/backend.js";
import Page from "../components/layout/Page.jsx";
import ProductList from "../components/ProductList";
import SidePanel from "../components/SidePanel.jsx";
import { Alert, Button, Card, EmptyState, Spinner } from "../components/ui.jsx";
import { useOccurrence } from "../context/OccurrenceContext.jsx";
import { requestParentScroll, useIframeHeight } from "../hooks/useIframeHeight.js";
import { actionLabel, expandLineItems, resolveProductAction } from "../utils/orderItems.js";
import styles from "./Products.module.css";

const Products = () => {
  const { shop, name, email } = useParams();
  const { store, order, setOrder, setOccurrence, setCustomerEmail } = useOccurrence();
  const [selectedAction, setSelectedAction] = useState({});
  const [selectedReason, setSelectedReason] = useState({});
  const [selectedExplication, setSelectedExplication] = useState({});
  const [loading, setLoading] = useState(!order);
  const [loadingButton, setLoadingButton] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const units = useMemo(() => expandLineItems(order), [order]);

  useIframeHeight();

  useEffect(() => {
    if (order) {
      setLoading(false);
      return undefined;
    }
    if (!name || !email) {
      navigate(`/${shop}`);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchOrder(shop, name, email)
      .then((next) => {
        if (!cancelled) {
          setOrder(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          navigate(`/${shop}`);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [email, name, navigate, order, setOrder, shop]);

  const getSelectedProducts = useCallback(() => {
    const entityMetafieldsDimensions = store?.optionsOms?.entityMetafieldDimensions;
    return units.flatMap(({ skuId, line }) => {
      if (!selectedAction[skuId] || selectedAction[skuId] === "Manter") {
        return [];
      }
      const currentVariant = (line.variants || []).find((variant) => variant.id === line.variant_id) || {};
      return [{
        sku: line.sku,
        sku_id: skuId,
        name: line.name,
        title: line.title,
        variantTitle: line.variant_title,
        action: resolveProductAction(line, selectedAction[skuId], order),
        reason: selectedReason[skuId],
        explication: selectedExplication[skuId],
        price: Number(line.price),
        image: line.image,
        newOrderVariantId: null,
        credit: false,
        variant_id: line.variant_id,
        product_id: line.product_id,
        grams: line.grams,
        availableVariants: line.availableVariants,
        options: line.options,
        onlineStorePreviewUrl: line.onlineStorePreviewUrl,
        hasWarranty: line.hasWarranty,
        dimensions: {
          length: entityMetafieldsDimensions === "variant" ? currentVariant.length : line.length,
          width: entityMetafieldsDimensions === "variant" ? currentVariant.width : line.width,
          height: entityMetafieldsDimensions === "variant" ? currentVariant.height : line.height,
        },
      }];
    });
  }, [order, selectedAction, selectedExplication, selectedReason, store, units]);

  const handleClick = () => {
    setMessage("");
    setLoadingButton(true);
    const selectedProducts = getSelectedProducts();

    if (selectedProducts.length === 0) {
      setMessage("Selecione ao menos um item.");
      setLoadingButton(false);
      return;
    }

    if (selectedProducts.some((item) => !item.reason)) {
      setMessage("Informe o motivo de cada item selecionado.");
      setLoadingButton(false);
      return;
    }

    const customerEmail = decodeURIComponent(email || "");
    setCustomerEmail(customerEmail);
    setOccurrence({
      id: order.id,
      name: order.name?.replace("#", ""),
      idShopify: shop,
      email: customerEmail,
      customer_email: customerEmail,
      products: selectedProducts,
      customer_id: order.customer?.id ?? order.customer?.default_address?.customer_id,
      shipping_address: order.shipping_address,
      billing_address: order.billing_address,
      shipping_reverse_free: order.shipping_reverse_free,
      carrier: order?.shipping_lines?.[0]?.title,
    });
    navigate(`/${shop}/resolution/${name}/${encodeURIComponent(email)}`);
    requestParentScroll();
    setLoadingButton(false);
  };

  if (loading) {
    return (
      <Page title="Itens do pedido" step="items" onBack={() => navigate(`/${shop}`)}>
        <Card><Spinner label="Carregando seus itens..." /></Card>
      </Page>
    );
  }

  const chosenUnits = units.filter(({ skuId }) => selectedAction[skuId] && selectedAction[skuId] !== "Manter");
  const aside = (
    <SidePanel
      title={chosenUnits.length ? `${chosenUnits.length} item(ns) selecionado(s)` : "Nenhum item selecionado"}
      items={chosenUnits.map(({ skuId, line }) => ({
        key: skuId,
        image: line.image,
        title: line.title,
        meta: line.variant_title,
        price: line.price,
      }))}
      note="Marque os itens, conte o motivo e continue. Na próxima etapa você escolhe como resolver."
    >
      {message ? <Alert>{message}</Alert> : null}
      <Button onClick={handleClick} loading={loadingButton} disabled={units.length === 0}>
        Continuar
      </Button>
    </SidePanel>
  );

  return (
    <Page
      aside={aside}
      title="Itens do pedido"
      subtitle={order?.customer?.first_name ? `Olá, ${order.customer.first_name}. Selecione o que deseja devolver ou trocar.` : "Selecione os itens e o motivo."}
      step="items"
      onBack={() => navigate(`/${shop}`)}
    >
      <div className={styles.stack}>
        {units.length === 0 ? (
          <Card>
            <EmptyState
              title="Nenhum item disponível"
              description="Este pedido não tem itens elegíveis para troca ou devolução."
            />
          </Card>
        ) : (
          <ProductList
            units={units}
            selectedAction={selectedAction}
            handleSelectedAction={(sku, value) => setSelectedAction((prev) => ({ ...prev, [sku]: value }))}
            selectedReason={selectedReason}
            handleSelectedReason={(sku, value) => setSelectedReason((prev) => ({ ...prev, [sku]: value }))}
            getActionName={actionLabel}
            selectedExplication={selectedExplication}
            handleSelectedExplication={(sku, value) => setSelectedExplication((prev) => ({ ...prev, [sku]: value }))}
          />
        )}
      </div>
    </Page>
  );
};

export default Products;
